/**
 * @fileoverview Write a page, see it, publish it.
 *
 * EDITING AN EXISTING PAGE IS THE CASE THIS IS SHAPED AROUND, not an extra
 * bolted onto a "new page" form. A page is a thing that gets revised, its
 * address is permanent precisely so that it can be, and the awkward decisions
 * all live on the edit side. Two of them are load-bearing:
 *
 * THE ADDRESS STOPS BEING EDITABLE THE MOMENT THE PAGE EXISTS. Changing a
 * published page's name does not rename it. It abandons the page at its old
 * address -- still live, still the one every shared link points at -- and
 * starts a second one nowhere near it. There is no rename, so there is no field
 * offering one.
 *
 * THE FORMAT AND THE VISIBILITY BOTH STOPPED CHANGING THE ADDRESS ON
 * 2026-09-16, and two locks on this screen went with them. Until that ruling a
 * markdown page was a long-form event while a raw HTML page and a private page
 * were our own kind, so switching either setting moved the page to a different
 * kind, therefore a different address, stranding every link and leaving the old
 * version published under the old name. Every page is now a 33301, so both
 * switches republish at the same coordinate. The format lock is gone and so is
 * the refusal to flip a published markdown page to private; what remains of the
 * visibility warning is the part that was never about the address, and
 * `VisibilityChoice` still says it.
 *
 * ANYTHING THIS EDITOR DOES NOT UNDERSTAND IS CARRIED THROUGH UNTOUCHED. A page
 * may already carry a theme reference and layout hints this screen shows no
 * control for. An editor that republished only the fields it has inputs for
 * would silently strip them, which is data loss wearing the clothes of a save.
 * They are loaded into state, left alone, and written back.
 *
 * MARKDOWN IS THE DEFAULT AND RAW HTML IS THE ESCAPE HATCH, and the reason for
 * that changed on the same day without the decision changing. It used to be that
 * a markdown page was an ordinary long-form article six of seven measured
 * clients render for free; that is no longer true of anything we publish, and
 * copy resting on it would now be false. What markdown buys instead is
 * everything that makes a page a page here: it goes through our renderer, a
 * theme can style it, it stays editable in this editor, and it exports as
 * itself. Raw HTML gets none of that -- it is a file in a sandbox that cannot
 * be themed and cannot be meaningfully re-edited. So the two are still not a
 * pair of tabs. Markdown is simply what the editor is, and raw HTML is one
 * folded line underneath that has to be opened, read and confirmed.
 *
 * THE PREVIEW IS THE READER'S OWN RENDERER. Markdown goes through the same
 * sanitising renderer the page route uses and raw HTML goes into the same
 * sandboxed frame. A second renderer built for the editor would drift from the
 * first, and the drift would show up as a page that looked right while being
 * written and wrong once published.
 */

import type { ContentKey, SignerInterface } from '@cloistr/auth';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { PageBody } from './PageBody';
import { PublishResult } from './PublishResult';
import { SandboxedHtml } from './SandboxedHtml';
import { ShareControl } from './ShareControl';
import { TableOfContents } from './TableOfContents';

import { decodeIdentifier } from '@/services/nostr/identifiers';
import type { PageFormat, PageLayout } from '@/services/pages/indexEvent';
import { PAGE_THEME_KIND } from '@/services/pages/kinds';
import {
  publishPage,
  type PageAddress,
  type PublishOutcome,
} from '@/services/pages/publishPage';
import { isValidIdentifier, suggestedIdentifier } from '@/services/pages/slug';
import { parseThemeAddress } from '@/services/pages/theme';
import { renderMarkdown } from '@/services/render/markdown';

/** Everything a page carries, including the parts this screen does not show. */
export interface EditorDraft {
  identifier: string;
  title: string;
  summary: string;
  format: PageFormat;
  body: string;
  layout: PageLayout;
  themeAddress: string | null;
  publishedAt: number | null;
  /** Private pages are encrypted end to end; see `VisibilityChoice` below. */
  private: boolean;
  /**
   * The key an existing private page is sealed under, loaded with the page.
   *
   * Null for a public page and for one that does not exist yet. Saving a private
   * page WITHOUT it is refused by the publisher rather than served with a fresh
   * key, because a fresh key revokes every recipient and says nothing.
   */
  pageKey: ContentKey | null;
}

export function emptyDraft(): EditorDraft {
  return {
    // SEEDED, NOT LEFT BLANK, and never derived from the title. The name is the
    // one part of a page that can never be revisited, so it is not something to
    // generate from whatever the author happened to type first. An author who
    // wants a readable address types one; nothing proposes it for them.
    identifier: suggestedIdentifier(),
    title: '',
    summary: '',
    format: 'markdown',
    body: '',
    layout: { toc: false, width: 'prose', columns: 1 },
    themeAddress: null,
    publishedAt: null,
    // PUBLIC IS THE DEFAULT and it is the honest one: this is a publishing tool.
    // A default of private would make every page carry a key nobody asked for,
    // and every reader who is not holding it would get the no-access screen for
    // a page the author believed they had published. The reason used to be
    // stated as protecting the flagship case, an article other people's clients
    // render for free; since 2026-09-16 nothing we publish renders in those
    // clients, so that half of it is gone and the default is not.
    private: false,
    pageKey: null,
  };
}

/**
 * How long the preview lags the keyboard.
 *
 * Rendering is a full parse and sanitise of the whole document, and running it
 * on every keystroke makes a long page type badly. Short enough that it still
 * reads as live.
 */
const PREVIEW_DELAY_MS = 180;

/**
 * THERE IS NO LIVE SIZE WARNING HERE ANY MORE, AND ITS ABSENCE IS THE POINT.
 *
 * This screen used to measure the draft on every keystroke and warn at four
 * fifths of the relay's maximum message size, because a public markdown page
 * travelled INSIDE its event and a long one could cross the ceiling -- where the
 * relay's answer is to close the socket silently, which every client library
 * reports as a generic network error.
 *
 * Since 2026-09-16 every page's body is a blob and the event carries a
 * fixed-length hash instead. The event is a few hundred bytes whatever the
 * author writes, so the warning could no longer fire for any page a person could
 * type. A meter that cannot move is worse than no meter: it reads as an
 * assurance that something is being watched.
 *
 * THE PUBLISHER'S PRE-CHECK STAYS, and it is not the same thing. It measures the
 * real event against the real relay's advertised limit before signing, costs
 * nothing, and is the only thing standing between an author and that silent
 * socket close if some future tag ever does grow without bound.
 */

/**
 * THE AUTHOR'S PUBLIC KEY IS NO LONGER A PROP. It was here only to build a
 * draft event for the size meter, which needed a pubkey to serialise. The
 * publisher asks the signer for it directly, so nothing else on this screen ever
 * wanted it, and a prop kept alive for a deleted feature is a question the next
 * reader has to answer.
 */
export function PageEditor({
  signer,
  initial,
  initialAddress,
  onPublished,
}: {
  signer: SignerInterface;
  initial: EditorDraft;
  /** The page being edited, or null when this is a page that does not exist yet. */
  initialAddress: PageAddress | null;
  onPublished: (address: PageAddress) => void;
}) {
  const [draft, setDraft] = useState<EditorDraft>(initial);
  const [publishing, setPublishing] = useState(false);
  const [outcome, setOutcome] = useState<PublishOutcome | null>(null);

  // THE MOMENT A NEW PAGE STOPS BEING NEW. It is held here rather than read
  // from the route on every render because the change happens without a
  // navigation: the author is still looking at their text and has not gone
  // anywhere. Everything that follows from a page existing follows from this --
  // the address and the format stop being editable, the button stops saying
  // Publish, and the NEXT save is an edit of this page rather than an attempt
  // to create a second one under a name that is now taken. Without it, pressing
  // Publish twice would be refused by the page it had just made.
  const [editingAddress, setEditingAddress] = useState<PageAddress | null>(initialAddress);

  const existing = editingAddress !== null;

  function update(patch: Partial<EditorDraft>) {
    setDraft((d) => ({ ...d, ...patch }));
    // A result describes the draft that produced it. Once the draft moves, it
    // is stale, and a success notice hanging over edited text reads as "saved".
    setOutcome(null);
  }

  function onTitle(title: string) {
    // THE TITLE DOES NOT TOUCH THE ADDRESS. It used to fill it on every
    // keystroke, which made the single irreversible field in the product a
    // by-product of typing. See `slug.ts` for the ruling.
    update({ title });
  }

  // --- the preview, one beat behind the keyboard ---

  const [previewBody, setPreviewBody] = useState(initial.body);
  useEffect(() => {
    const timer = setTimeout(() => setPreviewBody(draft.body), PREVIEW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [draft.body]);

  const rendered = useMemo(
    () => (draft.format === 'markdown' ? renderMarkdown(previewBody) : null),
    [previewBody, draft.format]
  );

  // --- publishing ---

  const identifierOk = isValidIdentifier(draft.identifier);
  const canPublish = !publishing && draft.title.trim() !== '' && identifierOk;

  async function onPublish() {
    setPublishing(true);
    setOutcome(null);
    const result = await publishPage({ ...draft, replaces: editingAddress }, signer);
    setOutcome(result);
    setPublishing(false);
    if (result.status === 'published') {
      setEditingAddress(result.address);
      // A NEW private page's key exists only in the publisher's return value
      // until it is held here. Losing it would mean the very next save arrived
      // without it and was refused -- correctly, and infuriatingly, one second
      // after the page was created.
      if (result.pageKey) setDraft((d) => ({ ...d, pageKey: result.pageKey as ContentKey }));
      onPublished(result.address);
    }
  }

  return (
    <div className="editor">
      <div className="editor-form">
        <h1 className="editor-heading">{existing ? 'Edit page' : 'Write a page'}</h1>

        <label className="editor-field">
          <span className="editor-label">Title</span>
          <input
            className="editor-input"
            value={draft.title}
            onChange={(e) => onTitle(e.target.value)}
            placeholder="What is this page called?"
          />
        </label>

        <label className="editor-field">
          <span className="editor-label">Summary</span>
          <input
            className="editor-input"
            value={draft.summary}
            onChange={(e) => update({ summary: e.target.value })}
            placeholder="One line, shown in listings and search"
          />
        </label>

        {/* The address. Editable exactly once in a page's life. */}
        <div className="editor-field">
          <span className="editor-label">Address</span>
          {existing ? (
            <>
              <p className="editor-fixed">{draft.identifier}</p>
              <p className="editor-note">
                A published page&rsquo;s address cannot be changed. Changing it would not
                rename this page, it would leave this one where it is and start a second
                one somewhere else.
              </p>
            </>
          ) : (
            <>
              <input
                className="editor-input"
                value={draft.identifier}
                onChange={(e) => update({ identifier: e.target.value })}
                placeholder="lowercase-words-with-hyphens"
                aria-invalid={draft.identifier !== '' && !identifierOk}
              />
              <p className="editor-note">
                {draft.identifier !== '' && !identifierOk
                  ? 'Lowercase letters, numbers and hyphens only.'
                  : 'This name is permanent and public, even if the page is not. Replace it with your own if you want a readable address.'}
              </p>
            </>
          )}
        </div>

        <VisibilityChoice
          isPrivate={draft.private}
          existing={existing}
          onChange={(isPrivate) => update({ private: isPrivate })}
        />

        <label className="editor-field editor-field-grow">
          <span className="editor-label">
            {draft.format === 'markdown' ? 'Page (Markdown)' : 'Page (raw HTML)'}
          </span>
          <textarea
            className="editor-body"
            value={draft.body}
            onChange={(e) => update({ body: e.target.value })}
            spellCheck={draft.format === 'markdown'}
            placeholder={
              draft.format === 'markdown' ? '# A heading\n\nSome prose.\n' : '<h1>A heading</h1>\n'
            }
          />
        </label>

        {draft.format === 'markdown' && (
          <label className="editor-check">
            <input
              type="checkbox"
              checked={draft.layout.toc}
              onChange={(e) => update({ layout: { ...draft.layout, toc: e.target.checked } })}
            />
            <span>Show a table of contents</span>
          </label>
        )}

        <ThemeChoice
          themeAddress={draft.themeAddress}
          onChange={(themeAddress) => update({ themeAddress })}
        />

        <FormatEscapeHatch
          format={draft.format}
          published={existing}
          onSwitch={(format) => update({ format })}
        />

        <button className="editor-publish" onClick={() => void onPublish()} disabled={!canPublish}>
          {publishing ? 'Publishing…' : existing ? 'Publish the changes' : 'Publish'}
        </button>

        {outcome && <PublishResult outcome={outcome} />}

        {/* AFTER THE FIRST PUBLISH AND NOT BEFORE, because a share names an
            address and there is no address until the page exists. Only for a
            private page: sharing a key to a page anybody can already read
            would be a control with no effect, offered next to one that has
            one. */}
        {editingAddress && draft.private && draft.pageKey && (
          <ShareControl
            subject="page"
            address={`${editingAddress.kind}:${editingAddress.pubkey}:${editingAddress.identifier}`}
            contentKey={draft.pageKey}
            signer={signer}
          />
        )}
      </div>

      <div className="editor-preview">
        <p className="editor-preview-label">Preview</p>
        <article
          className="page"
          data-width={draft.layout.width}
          data-columns={draft.layout.columns}
        >
          <header className="page-header">
            <h1 className="page-title">{draft.title || 'Untitled'}</h1>
            {draft.summary && <p className="page-summary">{draft.summary}</p>}
          </header>
          {rendered ? (
            <>
              {draft.layout.toc && <TableOfContents headings={rendered.headings} />}
              <PageBody fragment={rendered.fragment} />
            </>
          ) : (
            <SandboxedHtml html={previewBody} title={draft.title || 'Untitled'} />
          )}
        </article>
      </div>
    </div>
  );
}

/**
 * Who can read this page, and what flipping it does NOT do.
 *
 * THE HARD PART IS NOT THE SWITCH, IT IS WHAT IT CANNOT UNDO, and the
 * operator's ruling is that each of those is said plainly at the moment of
 * flipping rather than in documentation nobody reads.
 *
 * 1. GOING PRIVATE IS NOT A RETRACTION. A public page's body was served openly
 *    to anyone who asked, it is content-addressed, and it may have been copied,
 *    cached or mirrored. Encrypting it from now on changes the next version and
 *    nothing about the ones already out there. Measured, not feared: a public
 *    gateway that has been asked for a page once keeps serving it after our own
 *    relay has been told to delete it.
 * 2. THE NAME IS PUBLIC EVEN WHEN THE PAGE IS NOT. It is part of the address, so
 *    it cannot be encrypted, and it cannot be changed without abandoning the
 *    page. That is why the suggested name is sixteen random characters and never
 *    comes from the title.
 *
 * THERE WAS A THIRD, AND IT IS GONE RATHER THAN MITIGATED. Until 2026-09-16 a
 * public markdown page was a NIP-23 long-form event while a private one was our
 * own kind, so flipping one changed its kind, therefore its address: the new
 * version landed somewhere else and the old event stayed exactly where it was --
 * still published, still plaintext, still the target of every shared link. This
 * screen refused that flip outright and said why, because a half-flip that looks
 * like it worked leaves an author worse off than before they pressed the button
 * and with no way to know.
 *
 * EVERY PAGE IS A 33301 NOW, so the flip changes a property of the event and
 * nothing about where it lives. Both directions are offered for every format,
 * the address survives, and the refusal branch is deleted rather than left
 * standing on a reason that expired. The NIP-09 deletion request that would have
 * been needed to do the old flip properly is no longer owed for this.
 */
function VisibilityChoice({
  isPrivate,
  existing,
  onChange,
}: {
  isPrivate: boolean;
  existing: boolean;
  onChange: (isPrivate: boolean) => void;
}) {
  if (!existing) {
    return (
      <div className="editor-field">
        <span className="editor-label">Who can read this</span>
        <label className="editor-check">
          <input
            type="radio"
            name="visibility"
            checked={!isPrivate}
            onChange={() => onChange(false)}
          />
          <span>Anyone with the link</span>
        </label>
        <label className="editor-check">
          <input
            type="radio"
            name="visibility"
            checked={isPrivate}
            onChange={() => onChange(true)}
          />
          <span>Only people I share it with</span>
        </label>
        {isPrivate ? (
          <p className="editor-note">
            The page is encrypted before it leaves this browser, and the key travels to each
            person you share it with. Nobody else can read it, including us. Its address and
            its name are still public: anyone holding the link can tell that a page exists
            there, and cannot tell what it says.
          </p>
        ) : (
          <p className="editor-note">
            Anyone holding the address can read it.
          </p>
        )}
      </div>
    );
  }

  if (isPrivate) {
    return (
      <details className="editor-escape">
        <summary>This page is private. Make it public?</summary>
        <p>
          Anyone holding the address would be able to read it. The address does not change,
          so every link already shared starts working for everyone rather than only for the
          people you gave the key to.
        </p>
        <button className="editor-escape-switch" onClick={() => onChange(false)}>
          Make this page public
        </button>
      </details>
    );
  }

  return (
    <details className="editor-escape">
      <summary>This page is public. Make it private?</summary>
      <p>
        <strong>This does not take back what is already published.</strong> This page has
        been served openly to anyone who asked for it, and it may have been copied, cached
        or mirrored by people and services we have no reach over. Making it private
        encrypts it from the next save onwards. It does not retract the version that is
        already out there.
      </p>
      <p>
        The address does not change, so links people already hold keep working &mdash; for
        the people you share the key with. The page&rsquo;s name stays public whether or not
        the page is, because the name is part of the address and cannot be encrypted.
      </p>
      <button className="editor-escape-switch" onClick={() => onChange(true)}>
        Make this page private from now on
      </button>
    </details>
  );
}

/**
 * Which theme this page wears, if any.
 *
 * THE FIELD TAKES A COORDINATE OR AN `naddr` AND STORES ONLY A COORDINATE,
 * because that is what the `theme` tag carries and what every reader parses. An
 * editor that stored whichever form the author pasted would put a bech32 string
 * in a tag readers resolve as three colon-separated parts, and the page would
 * render unthemed with nothing saying why -- the same silent failure the theme
 * editor exists to make visible, arriving one level up.
 *
 * AN ADDRESS OF ANOTHER KIND IS REFUSED HERE, before it can be saved. The
 * reader already refuses it before it queries, so that a page cannot use its
 * theme tag to make a stranger's browser fetch an arbitrary event. This refusal
 * is not that one: it is for the author, who otherwise pastes a page's address
 * into the theme box -- an easy mistake, since both are `naddr`s -- and gets a
 * page that looks exactly as it did before.
 *
 * A REFUSED PASTE DOES NOT CLEAR THE THEME THE PAGE ALREADY HAS. Clearing on a
 * bad paste would mean a typo silently unstyles a published page, and the
 * author would be looking at the box they just typed into rather than at the
 * styling they did not touch. Emptying the box is how a theme is removed,
 * because that is an act rather than an accident.
 */
function ThemeChoice({
  themeAddress,
  onChange,
}: {
  themeAddress: string | null;
  onChange: (themeAddress: string | null) => void;
}) {
  const [text, setText] = useState(themeAddress ?? '');

  const problem = (() => {
    const raw = text.trim();
    if (raw === '') return null;
    return normaliseThemeReference(raw) === null ? describeThemeProblem(raw) : null;
  })();

  function onText(next: string) {
    setText(next);
    const raw = next.trim();
    if (raw === '') {
      onChange(null);
      return;
    }
    const coordinate = normaliseThemeReference(raw);
    // Only a good one moves the draft. See the header: a bad paste must not
    // unstyle a page that is already published.
    if (coordinate) onChange(coordinate);
  }

  return (
    <div className="editor-field">
      <span className="editor-label">Theme</span>
      <input
        className="editor-input"
        value={text}
        onChange={(e) => onText(e.target.value)}
        placeholder="A theme's address, or leave empty for default styling"
        aria-invalid={problem !== null}
      />
      <p className="editor-note">
        {problem ?? (
          <>
            One theme can be worn by many pages, so editing it restyles all of them.{' '}
            <Link to="/theme">Make one</Link>, or paste the address of any theme — including
            somebody else&rsquo;s, which means trusting them to keep it as it is.
          </>
        )}
      </p>
    </div>
  );
}

/**
 * A theme reference in either form, as the coordinate the tag carries, or null.
 *
 * `parseThemeAddress` is the reader's own parser, so the forms this accepts and
 * the forms a reader resolves cannot drift apart.
 */
function normaliseThemeReference(raw: string): string | null {
  const direct = parseThemeAddress(raw);
  if (direct) return direct.kind === PAGE_THEME_KIND ? raw.trim() : null;

  let decoded;
  try {
    decoded = decodeIdentifier(raw);
  } catch {
    // An nsec. Refused without being echoed anywhere, including into the
    // message below, which is why that message never repeats the input.
    return null;
  }
  if (decoded?.type !== 'address') return null;
  if (decoded.kind !== PAGE_THEME_KIND) return null;
  return `${decoded.kind}:${decoded.pubkey}:${decoded.identifier}`;
}

/** Why a reference was refused, without ever repeating what was pasted. */
function describeThemeProblem(raw: string): string {
  let decoded = null;
  try {
    decoded = decodeIdentifier(raw);
  } catch {
    return 'That is a private key. Never paste it anywhere, and treat it as compromised.';
  }
  const other = parseThemeAddress(raw);
  if (decoded?.type === 'address' || other) {
    return 'That address names something that is not a theme — a page, perhaps. A page wearing it would render unstyled.';
  }
  return 'That is not a theme address. Publish a theme and copy the address it gives you.';
}

/**
 * The way to raw HTML, and the way back.
 *
 * DELIBERATELY NOT A PAIR OF TABS. Two tabs say the formats are equals and that
 * picking between them is a matter of taste. They are not equals, and the reason
 * they are not changed on 2026-09-16 without the conclusion changing. It used to
 * be that markdown published as an ordinary long-form article other clients
 * render and HTML did not; nothing we publish renders in those clients now, so
 * that sentence would be false on the screen. What markdown buys instead is
 * everything this product does to a page: our renderer, a theme, an editor that
 * can open it again, an export that is the source. Raw HTML is a file in a
 * sandbox and gets none of it. So the hatch stays folded shut, it states the
 * cost before it offers the switch, and opening it is a decision.
 *
 * IT OPENS ON A PUBLISHED PAGE TOO, which it did not before. The lock existed
 * because the two formats were two different kinds, so a switch moved the page
 * to a different address and stranded every link. Both formats are a 33301 now,
 * the coordinate is the same either way, and a lock whose reason has expired is
 * worse than no lock: the next person reads it as protecting something.
 */
function FormatEscapeHatch({
  format,
  published,
  onSwitch,
}: {
  format: PageFormat;
  published: boolean;
  onSwitch: (format: PageFormat) => void;
}) {
  if (format === 'html') {
    return (
      <div className="editor-escape editor-escape-open">
        <p>
          This page is raw HTML. It is stored as a file and shown inside a sandbox that
          cannot run scripts, so a theme cannot style it and this editor cannot do much
          more than hold the text.
        </p>
        <button className="editor-escape-back" onClick={() => onSwitch('markdown')}>
          Go back to Markdown
        </button>
        {published && (
          <p className="editor-note">
            The page&rsquo;s address does not change, so every link already shared keeps
            working. What changes is how the text below is read: switch back and it is
            treated as Markdown from the next save onwards.
          </p>
        )}
      </div>
    );
  }

  return (
    <details className="editor-escape">
      <summary>Write raw HTML instead</summary>
      <p>
        Markdown is the right answer for almost every page. It goes through this
        app&rsquo;s renderer, so a theme can style it, this editor can open it again, and
        exporting it gives you back the source you wrote.
      </p>
      <p>
        Raw HTML is for the pages Markdown cannot express. It is shown in a sandbox with
        scripts disabled, no theme applies to it, and it comes back out of an export as
        the file it is.
      </p>
      {published && (
        <p>
          Switching a published page does not move it: the address stays the same and every
          link already shared keeps working. The text below is simply read as HTML from the
          next save onwards.
        </p>
      )}
      <button className="editor-escape-switch" onClick={() => onSwitch('html')}>
        Switch this page to raw HTML
      </button>
    </details>
  );
}
